import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { ComponentView } from 'src/modules/app-template/models/component-view.model';
import { RestEndpoint } from 'src/modules/sm-base/models/rest-endpoint.model';
import { TableCell } from 'src/modules/sm-base/models/table-cell.model';
import { TableData } from 'src/modules/sm-base/models/table-data.model';
import { TableRow } from 'src/modules/sm-base/models/table-row.model';
import { TableCellType } from 'src/modules/sm-base/netgen/TableCellType';
import { TableColumn } from 'src/modules/sm-base/netgen/TableColumn';
import { Arrays } from 'src/modules/utils/shared/arrays';
import { ObjectOfSimpleDataTypes } from 'src/modules/utils/shared/object-of-simple-data-types.model';
import { Objects } from 'src/modules/utils/shared/objects';
import { OrdinaryObject } from 'src/modules/utils/shared/ordinary-object.model';
import { EnaioCertificateService } from '../../services/enaio-certificate.service';
import { Competency } from '../../shared/competency.model';
import { EnaioCertificatesTools } from '../../shared/enaio-certificates-tools';
import { FinalGrade } from '../../shared/final-grade.entity';
import { GlobalSettings } from '../../shared/global-settings.entity';
import { GradeBookSchoolClass } from '../../shared/grade-book-school-class.entity';
import { Pupil } from '../../shared/pupil.model';
import { SchoolClass } from '../../shared/school-class.model';
import { SchoolTypeSettings } from '../../shared/school-type-settings.entity';
import { Utils } from 'src/modules/utils/shared/utils';

@Component({
  selector: 'app-final-exam-matrix',
  standalone: false,
  templateUrl: './final-exam-matrix.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './final-exam-matrix.component.scss',
})
export class FinalExamMatrixComponent extends ComponentView {

    service = inject(EnaioCertificateService);

    neededParams: ObjectOfSimpleDataTypes = { pupilId: "number", schoolClassId: "number" };
    pupilId: number;
    schoolClassId: number;

    year: number;

    pupil: Pupil;    
    schoolClass: SchoolClass;
    gradeBookSchoolClass: GradeBookSchoolClass;
    domains: string[];
    finalGrades: FinalGrade[];
    grades: OrdinaryObject<number>;

    table: TableData;


    override async initParams(): Promise<boolean> {
        this.fullScreenComponent = false;

        this.year = EnaioCertificatesTools.getCurrentYear();

        this.pupil = await this.service.restGetPupil(this.pupilId);

        this.schoolClass = await this.service.restGetSchoolClass(this.schoolClassId);
        this.gradeBookSchoolClass = await RestEndpoint.main().find({ schoolClassId: this.schoolClassId, isHalfYear: 0 }).run("api/cer/gradebookschoolclass").get(GradeBookSchoolClass);

        let globalSettings = await RestEndpoint.main().find({}).run("api/cer/globalsettings").get(GlobalSettings);

        let settings = await RestEndpoint.main().find({schoolType: this.schoolClass.getSchoolType(), year: this.year, isHalfYear: false}).run("api/cer/schooltypesettings").get(SchoolTypeSettings);
        this.grades = this.gradeBookSchoolClass.getPupilAverageMap(globalSettings, settings, this.schoolClass, null, [this.pupil]);
        for (let key of Objects.getPropertyNames(this.grades)) {
            if (this.grades[key] != null) {
                this.grades[key] = Utils.truncateDecimals(this.grades[key], 1);
            }
        }

        let finalGrades = (await RestEndpoint.main().find({ year: this.year }).run("api/cer/finalgrade").list(FinalGrade)).filter(fg => fg.pupilId == this.pupilId);

        let competencies = await RestEndpoint.main().find({ schoolClassId: this.schoolClass.id }).run("api/cer/competency").list(Competency);   

        this.domains = await this.service.restGetFinalExamDomains(Arrays.getUnique(competencies.map(c => c.domain)));

        this.finalGrades = this.domains.map(domain => finalGrades.find(fg => fg.domain == domain) ?? Objects.fromPlain(FinalGrade, {
            pupilId: this.pupilId,
            year: this.year,
            domain
        }));

        this.fillTable();

        this.app.updateNavigation(null, [
            this.service.getHomeClassRegister(),
            { label: "Jahres- und Prüfungsnoten", routerLink: ["/enaio-certificates", "class-registers", "final-exams"] },
            { label: "Berechnungmatrix " + this.pupil.getFullName(), routerLink: ["/enaio-certificates", "class-registers", "final-exams", "matrix", this.pupilId, this.schoolClassId] }
        ]);

        return true;
    }

    fillTable(): void {
        this.table = new TableData([
            new TableColumn("domain", "Fach", TableCellType.string, 20),
            new TableColumn("note", "Bemerkung", TableCellType.string, 20),
            new TableColumn("yearGrade", "Jahresnote", TableCellType.decimal, 3, {
                rotate90: true,
                numDecimalDigits: 1,
                expandDecimalDigits: true,
                editable: true,
                align: "center"
            }),
            new TableColumn("writtenExamGrade", "sch. Prüf.", TableCellType.decimal, 3, {
                rotate90: true,
                numDecimalDigits: 1,                
                expandDecimalDigits: true,
                editable: true,
                align: "center"
            }),
            new TableColumn("oralExamGrade", "mdl. Prüf.", TableCellType.decimal, 3, {
                rotate90: true,
                numDecimalDigits: 1,
                expandDecimalDigits: true,
                editable: true,
                align: "center"
            }),
            new TableColumn("totalExamGrade", "Ergb.", TableCellType.decimal, 3, {
                rotate90: true,
                numDecimalDigits: 1,
                expandDecimalDigits: true,
                align: "center"  
            }),
            new TableColumn("finalGrade", "Endnote", TableCellType.decimal, 5, {
                rotate90: true,
                numDecimalDigits: 1,
                expandDecimalDigits: true,
                editable: true,
                align: "center",
                valueGetter: params => TableCell.getValue(params.row.values.finalGradeManuallyEdited) ?? params.row.values.finalGrade,
                valueSetter: params => {
                    if (params.value != TableCell.getValue(params.row.values.finalGradeManuallyEdited)) {
                        params.row.values.finalGradeManuallyEdited = params.value == params.row.values.finalGrade ? null : new TableCell(params.value);
                    }
                },
                getStyleClassCallback: params => TableCell.getValue(params.row.values.finalGradeManuallyEdited != null) ? "xbg-red-100" : ""
            }),
            new TableColumn("gp", "GP", TableCellType.string, 10, {
                groupCaption: "Gesamtprädikat",
                rotate90: true,
                numDecimalDigits: 1
            })
        ], this.updateRows(this.finalGrades.map(fg => new TableRow(fg.domain, fg, {
            domain: fg.domain,
            note: "",
            yearGrade: this.grades[this.pupil.id + "|" + fg.domain],
            writtenExamGrade: fg.writtenGrade,
            oralExamGrade: fg.oralGrade,
            totalExamGrade: 0,
            finalGrade: fg.finalGrade,
            finalGradeManuallyEdited: null,
            gp: ""
        }))));
    }

    updateRows(rows: TableRow[]) : TableRow[] {
        for (let row of rows) {
            let oralExamGrade = row.values.oralExamGrade as number;
            let writtenExamGrade = row.values.writtenExamGrade as number;
            let yearGrade = row.values.yearGrade  as number;
            let examGrade = writtenExamGrade == null ? oralExamGrade == null ? null : oralExamGrade : oralExamGrade == null ? writtenExamGrade : (oralExamGrade + writtenExamGrade) / 2;
            row.values.totalExamGrade =  yearGrade == null ? null : examGrade == null ? yearGrade : 0.6 * yearGrade + 0.4 * examGrade;
            let total = yearGrade == null ? null : examGrade == null ? yearGrade : 0.6 * yearGrade + 0.4 * examGrade;
            row.values.finalGrade = total != null ? Math.round(total) : null;
            row.values.gp = EnaioCertificatesTools.getFinalExamGp((TableCell.getValue(row.values.finalGradeManuallyEdited) ?? row.values.finalGrade) as number);
        }   
        return rows.map(row => new TableRow(row.id, row.raw, Objects.cloneDeep(row.values)));
    }

    valueChanged(): void {
        this.table = TableData.update(this.table, null, this.updateRows(this.table.rows));
    }
}
